"use client";

import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export interface ListParams {
  page: number;
  limit: number;
  search: string;
  sortBy: string;
  filters: Record<string, string>;
}

export interface UseListParamsOptions {
  defaultLimit?: number;
  defaultSort?: string;
  filterKeys?: readonly string[];
}

const PAGE_KEY = "page";
const LIMIT_KEY = "limit";
const SEARCH_KEY = "q";
const SORT_KEY = "sort";

const RESERVED = new Set([PAGE_KEY, LIMIT_KEY, SEARCH_KEY, SORT_KEY]);

/**
 * Persist list pagination, search, sort and filters in the URL via react-router
 * `useSearchParams`. Keeps the deep-link / refresh UX consistent across modules.
 */
export function useListParams(options: UseListParamsOptions = {}) {
  const { defaultLimit = 10, defaultSort = "recent", filterKeys = [] } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo<ListParams>(() => {
    const rawPage = Number(searchParams.get(PAGE_KEY));
    const rawLimit = Number(searchParams.get(LIMIT_KEY));
    const filters: Record<string, string> = {};
    filterKeys.forEach((key) => {
      const value = searchParams.get(key);
      if (value !== null) filters[key] = value;
    });
    return {
      page: Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1,
      limit: Number.isFinite(rawLimit) && rawLimit > 0 ? rawLimit : defaultLimit,
      search: searchParams.get(SEARCH_KEY) ?? "",
      sortBy: searchParams.get(SORT_KEY) ?? defaultSort,
      filters,
    };
  }, [searchParams, defaultLimit, defaultSort, filterKeys]);

  const updateParams = useCallback(
    (patch: Partial<ListParams> & { resetPage?: boolean }) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (patch.search !== undefined) {
            if (patch.search === "") next.delete(SEARCH_KEY);
            else next.set(SEARCH_KEY, patch.search);
          }
          if (patch.sortBy !== undefined) {
            if (patch.sortBy === defaultSort) next.delete(SORT_KEY);
            else next.set(SORT_KEY, patch.sortBy);
          }
          if (patch.limit !== undefined) {
            if (patch.limit === defaultLimit) next.delete(LIMIT_KEY);
            else next.set(LIMIT_KEY, String(patch.limit));
          }
          if (patch.filters) {
            Object.entries(patch.filters).forEach(([key, value]) => {
              if (RESERVED.has(key)) return;
              if (value === "" || value === undefined || value === null) {
                next.delete(key);
              } else {
                next.set(key, value);
              }
            });
          }
          if (patch.page !== undefined) {
            if (patch.page <= 1) next.delete(PAGE_KEY);
            else next.set(PAGE_KEY, String(patch.page));
          } else if (patch.resetPage) {
            next.delete(PAGE_KEY);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams, defaultLimit, defaultSort]
  );

  const setPage = useCallback(
    (page: number) => updateParams({ page }),
    [updateParams]
  );

  const setLimit = useCallback(
    (limit: number) => updateParams({ limit, resetPage: true }),
    [updateParams]
  );

  const setSearch = useCallback(
    (search: string) => updateParams({ search, resetPage: true }),
    [updateParams]
  );

  const setSort = useCallback(
    (sortBy: string) => updateParams({ sortBy, resetPage: true }),
    [updateParams]
  );

  const setFilter = useCallback(
    (key: string, value: string) =>
      updateParams({ filters: { [key]: value }, resetPage: true }),
    [updateParams]
  );

  return {
    ...params,
    setPage,
    setLimit,
    setSearch,
    setSort,
    setFilter,
  };
}
